/**
 * @file PromotionsPage.tsx
 * @description Акции-скидки каталога (phase18.3, promo.md P1–P3; phase19.31 —
 * каркас справочника (б) разбора B).
 *
 * Акция — скидка в процентах или рублях на весь каталог, категорию, товар
 * или фасовку, по всем каналам или по одному, с периодом действия. Таблица
 * со статусом по тону (колонки — `buildPromotionColumns`); «Включить /
 * Выключить» — в «⋯» строки и в форме, а не переключателем в строке.
 * Удаление — с подтверждением. Открытая на правку акция — в адресе `?edit=`.
 */

import { BadgePercent, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { PromotionEditDialog } from "../../../components/catalog/PromotionEditDialog";
import { useDirectoryList } from "../../../components/catalog/directory/useDirectoryList";
import { NEW_RECORD, useEditParam } from "../../../components/catalog/directory/useEditParam";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import type { RowAction } from "../../../components/ui/RowActionsMenu";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { catalogCategories, catalogPromotions, type CatalogCategory, type CatalogPromotion } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { buildPromotionColumns } from "./sections/PromotionsList";

export function PromotionsPage() {
  const notify = useNotify();
  const list = useDirectoryList(catalogPromotions.list, "Не удалось загрузить акции");
  const edit = useEditParam();
  const { confirm, request, settle } = useConfirm();
  const [categories, setCategories] = useState<CatalogCategory[]>([]);

  useEffect(() => {
    catalogCategories
      .list()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const columns = useMemo(() => buildPromotionColumns(new Map(categories.map((c) => [c.id, c]))), [categories]);
  const editing = edit.editId && !edit.isNew ? (list.items.find((p) => p.id === edit.editId) ?? null) : null;

  async function run(work: () => Promise<unknown>, done: string, failed: string) {
    try {
      await work();
      notify.success(done);
      await list.refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : failed);
    }
  }

  async function remove(p: CatalogPromotion) {
    const ok = await confirm({ title: `Удалить акцию «${p.displayName}»?`, description: "Скидка перестанет действовать сразу.", action: "Удалить", danger: true });
    if (ok) await run(() => catalogPromotions.remove(p.id), "Акция удалена", "Не удалось удалить акцию");
  }

  const rowActions = (p: CatalogPromotion): RowAction[] => [
    { key: "edit", label: "Изменить", icon: Pencil, run: () => edit.open(p.id) },
    {
      key: "toggle",
      label: p.isActive ? "Выключить" : "Включить",
      icon: Power,
      run: () => void run(() => catalogPromotions.update(p.id, { isActive: !p.isActive }), p.isActive ? "Акция выключена" : "Акция включена", "Не удалось изменить акцию"),
    },
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => void remove(p) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Акции"
        subtitle="Скидки на каталог, категорию, товар или фасовку"
        action={
          <Button variant="brand" onClick={() => edit.open(NEW_RECORD)}>
            <Plus className="size-4" />
            Акция
          </Button>
        }
      />
      <ResponsiveList
        aria-label="Акции"
        rows={list.items}
        columns={columns}
        rowKey={(p) => p.id}
        rowHref={(p) => edit.hrefFor(p.id)}
        rowLabel={(p) => `Акция ${p.displayName}`}
        rowActions={rowActions}
        rowActionsTitle={(p) => `Акция «${p.displayName}»`}
        tableMinWidth="min-w-[900px]"
        loading={list.loading}
        error={list.error}
        onRetry={() => void list.refresh()}
        empty={{ icon: BadgePercent, title: "Акций пока нет", description: "Создайте первую скидку — на весь каталог, категорию, товар или фасовку" }}
      />
      {edit.isNew || editing ? (
        <PromotionEditDialog
          open
          promotion={editing}
          categories={categories}
          onClose={edit.close}
          onSaved={() => {
            edit.close();
            void list.refresh();
          }}
        />
      ) : null}
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
